import {
  CONFLICT_RESOLUTIONS,
  VARIABLE_TYPES,
} from 'pages/SmartConnectors/constants';
import {
  ConflictResolutionMeta,
  FieldLike,
  FieldType,
  GenericOption,
  SmartConnectorMetaData,
  SmartConnectorVariable,
} from 'pages/SmartConnectors/types';
import { TFunction } from 'react-i18next';
import FieldService from '__services/FieldService';
import { getVariableTypeLabel } from '__pages/SmartConnectors/SmartConnector/helpers';

export const MAPPING_VIEW_OPTIONS = {
  mapped_fields: 'mapped_fields',
  fields_by_category: 'fields_by_category',
} as const;

export type MappingViewOption = keyof typeof MAPPING_VIEW_OPTIONS;

export const getMappingViewOptions = (
  t: TFunction
): { value: MappingViewOption; label: string }[] => [
  { value: MAPPING_VIEW_OPTIONS.mapped_fields, label: t('Mapped Fields') },
  {
    value: MAPPING_VIEW_OPTIONS.fields_by_category,
    label: t('Fields by Category'),
  },
];

const fallbackConflictResolution = {
  default: CONFLICT_RESOLUTIONS.only_update_blank,
  options: [CONFLICT_RESOLUTIONS.only_update_blank],
};

export const getConflictResolutionByFieldType = (
  metaData: SmartConnectorMetaData,
  field?: FieldLike
): ConflictResolutionMeta => {
  if (!field?.fieldType) {
    return fallbackConflictResolution;
  }

  const fieldType = ['name', 'email'].includes(field.name)
    ? 'required_field'
    : field.fieldType;

  const conflictResolutionMeta =
    metaData.field_mapping_rules.field_type_meta[fieldType]
      ?.conflict_resolution;

  if (
    conflictResolutionMeta?.allow_multiple &&
    FieldService.getAllowMultiple(field)
  ) {
    return conflictResolutionMeta.allow_multiple;
  }
  return conflictResolutionMeta || fallbackConflictResolution;
};

export const getFieldTypeMeta = (
  metaData: SmartConnectorMetaData,
  variable?: SmartConnectorVariable,
  fieldType?: FieldType
) => {
  return (
    variable?.data_type &&
    fieldType &&
    metaData.field_mapping_rules.variable_to_field_matching[
      variable.data_type
    ]?.[variable.is_array ? 'array' : 'single']?.[fieldType]
  );
};

export const getIsFieldMapToVariable = (
  field: FieldLike,
  variable: SmartConnectorVariable,
  metaData: SmartConnectorMetaData
) => {
  if (!field || !variable) {
    return false;
  }
  const fieldTypeMeta = getFieldTypeMeta(metaData, variable, field.fieldType);

  return (
    fieldTypeMeta &&
    (!fieldTypeMeta.only_allow_multiple || FieldService.getAllowMultiple(field))
  );
};

export type UnmappedVariableOption = GenericOption & {
  variable: SmartConnectorVariable;
  field: FieldLike;
};

export type UnmappedVariableWithFields = {
  variable: SmartConnectorVariable;
  fieldsForMapping: UnmappedVariableOption[];
};

const groupOrder = {
  [VARIABLE_TYPES.related_object_upload]: 0,
  [VARIABLE_TYPES.data_source]: 1,
  [VARIABLE_TYPES.additional]: 2,
};

export const getUnmappedVariablesGroupedOptions = (
  options: UnmappedVariableWithFields[],
  t: TFunction
) => {
  return options
    .reduce<{ label: string; options: UnmappedVariableWithFields[] }[]>(
      (acc, { variable, fieldsForMapping }) => {
        const index = groupOrder[variable.type] ?? 0;
        acc[index].options.push({ variable, fieldsForMapping });
        return acc;
      },
      [
        {
          label: getVariableTypeLabel('related_object_upload', t),
          options: [],
        },
        {
          label: getVariableTypeLabel('data_source', t),
          options: [],
        },
        {
          label: getVariableTypeLabel('additional', t),
          options: [],
        },
      ]
    )
    .filter(({ options }) => options.length > 0);
};
